import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { Fragment, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRowSelect, useSortBy, useTable } from "react-table";

import { CaseDocumentWithPresignedUrl, Patient, User } from "pwapi";

import { openDocument } from "shared/actions/caseDetailActions";

import Table from "components/admin/table/Table";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import ApplicationState from "store/ApplicationState";

import TableRow from "../ViewerListsTableView/TableRow";

type AdditionalDocument = CaseDocumentWithPresignedUrl & {
  case: { id: string; accession: string };
  patient: Patient;
  has_been_viewed: boolean;
};

const AdditionalDocumentsTable = ({
  documents,
}: {
  documents: AdditionalDocument[];
}) => {
  const dispatch = useDispatch();
  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Category",
        accessor: "category",
        Cell: ({ row }: any) => <>{row.original.category?.replace("-", " ")}</>,
      },
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ row }: any) => (
          <>
            {row.original.created_at &&
              format(new Date(row.original.created_at), "MM/dd/yyyy")}
          </>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }: any) => {
          const category =
            row.original.category ===
            CaseDocumentWithPresignedUrl.category.CASE_REPORT
              ? "Final Report"
              : row.original.category.replace("-", " ");
          let documentName = `${row.original.patient.last_name || ""}, ${
            row.original.patient.first_name?.[0] || ""
          } - ${row.original.case.accession} - ${category}`;

          return (
            <div
              className="cell__content--horizontal"
              style={{ gap: "0.5em", alignSelf: "flex-end" }}
            >
              {row.original.status !==
                CaseDocumentWithPresignedUrl.status.PENDING && (
                <>
                  <PathologyWatchButton
                    title="download"
                    onClick={() => {
                      row.original?.id &&
                        dispatch(
                          openDocument(
                            row.original.case.id,
                            row.original.id,
                            "document-list",
                            user,
                            documentName
                          )
                        );
                    }}
                  >
                    <>
                      {category} &nbsp;
                      <FontAwesomeIcon
                        icon="file-download"
                        color="var(--blue)"
                        style={{ fontSize: "1.2em" }}
                      />
                    </>
                  </PathologyWatchButton>
                </>
              )}
            </div>
          );
        },
      },
    ],
    []
  ) as any;

  const { headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: documents,
      autoResetExpanded: false,
      pageCount: -1,
      disableSortRemove: true,
      initialState: {
        sortBy: [
          {
            desc: false,
            id: "name",
          },
        ],
      },
    },

    useSortBy,
    useRowSelect
  );

  return (
    <Table
      className="DocumentTable"
      headerGroups={headerGroups.map((headerGroup, index) => (
        <tr {...headerGroup.getHeaderGroupProps()} key={index}>
          {headerGroup.headers.map((column: any) => (
            <th
              {...column.getHeaderProps(column.getSortByToggleProps())}
              className="Headers__cell"
              key={`column:${column.id}`}
            >
              {column.render("Header")}
              <span>
                {column.isSorted ? (
                  column.isSortedDesc ? (
                    <FontAwesomeIcon icon="angle-down" />
                  ) : (
                    <FontAwesomeIcon icon="angle-up" />
                  )
                ) : (
                  ""
                )}
              </span>
            </th>
          ))}
        </tr>
      ))}
      rows={rows.map((row) => {
        prepareRow(row);

        return (
          <Fragment
            {...row.getToggleRowExpandedProps}
            key={`doc:${row.original.id}`}
          >
            <TableRow
              row={row}
              isUnviewed={row.original && !row.original.has_been_viewed}
            />
          </Fragment>
        );
      })}
    />
  );
};

export default AdditionalDocumentsTable;
